import { useState, useEffect, useContext } from 'react';
import { v4 as uuid4 } from 'uuid';
import { IHeadCell } from "core/components/global/tables/datagrid/dataGridHeader/datagridHeader";
import { Filter } from "./customSort.types";
import { getOCSFSeverityNameFromNumber } from 'core/utils/common';
import CasesEmergency from "assets/CasesEmergeny"
import CasesHigh from "assets/CasesHigh";
import CasesMedium from "assets/CasesMedium";
import CasesLow from "assets/CasesLow";
import CasesInformational from "assets/CasesInformational";
import { AuthContext } from 'core/contexts/auth/auth';
import { useTranslation } from 'react-i18next';
import SideModal from './SideModal';

/**
 * Trying to make this component reusable
 * So any icons that should be added to headers can be added here
 * If this gets complicated we can instead inherit a list through props, start with this
 */
function getSelectedFilters(activeFilter) {
  // Aggregate list of children where isChecked is true
  const selectedFilters = {
    ...activeFilter,
    categories: activeFilter?.categories.map(category => ({
      ...category,
      children: category.children.filter(child => child.isChecked)
    }))
  } as Filter
  return selectedFilters
}

const filterIconMappings = {
  'severity': { 
    'informative': CasesInformational,
    'unclassified': CasesInformational, 
    'low': CasesLow, 
    'medium': CasesMedium, 
    'high': CasesHigh, 
    'fatal': CasesEmergency, 
    'critical': CasesEmergency
  }
}

interface CustomSortProps {
  table_headers: IHeadCell[];
  setFilterData: any;
  isFilterModalOpen: boolean;
  setIsFilterModalOpen: any;
  setPage: any;
  getFiltersFunction: any;
  framework?: string;
  applyFilters?: any;
  descriptions?: string[];
  selectedDescription?: string;
  selectedSectionTitle?: string;
  onFilterCreated?: any;
}

export default function CustomSort({
  table_headers,
  setFilterData,
  isFilterModalOpen,
  setIsFilterModalOpen,
  setPage,
  getFiltersFunction,
  framework,
  descriptions,
  selectedDescription,
  selectedSectionTitle,
  onFilterCreated
}: CustomSortProps) {
  const { checkAFToken } = useContext(AuthContext)
  const { t } = useTranslation();
  
  /**
   * Fetches the full list of active and inactive users on this platform
   */
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const token = await checkAFToken();
        const config = { 'Authorization': token, 'Content-Type': 'application/json' };      
        const filterResponse = await getFiltersFunction(config);
        setAvailableFilters(filterResponse);

      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    
    fetchFilters();
  }, []);

  const [filters, setFilters] = useState<Filter[]>([]);
  const [activeFilter, setActiveFilter] = useState<Filter | undefined>();
  const [underConstructionFilter, setUnderConstructionFilter] = useState<Filter>();
  const [availableFilters, setAvailableFilters] = useState<Record<string, any>>({});

  /**
   * Provide a new base filter object based on the provided headers and table data
   */
  const getBaseFilter = (exitingFilter?) => {
    const newFilter: Filter = {
      id: exitingFilter ? exitingFilter.id : uuid4(),
      name: exitingFilter ? exitingFilter.name : '',
      categories: table_headers.map((header) => {
        // If no filters available, return empty category
        if (!availableFilters || Object.keys(availableFilters).length === 0) {
          return {
            label: header.id,
            children: [],
            isDate: header.isDate || false,
            isFilterable: header.isFilterable
          };
        }

        let filterKey: string | undefined = undefined;
        
        // First check for exact match
        if (header.id in availableFilters) {
          filterKey = header.id;
        } else {
          // If no exact match, try case-insensitive match
          const availableFilterKeys = Object.keys(availableFilters);
          
          const lowerHeaderId = header.id.toLowerCase();
          for (const key of availableFilterKeys) {
            if (key.toLowerCase() === lowerHeaderId) {
              filterKey = key;
              break;
            }
          }
        }
        
        // If we found a match (either exact or case-insensitive)
        if (filterKey) {
          try {
            const filterData = availableFilters[filterKey];
            
            if (!Array.isArray(filterData)) {
              console.error(`Filter data for ${filterKey} is not an array:`, filterData);
              return {
                label: header.id,
                children: [],
                isDate: header.isDate || false,
                isFilterable: header.isFilterable
              };
            }
            
            const categoryChildren = Array.from(filterData).map((value: any) => ({
              // Standard Keys for 'FilterOptions' to use
              name: value,
              isChecked: false,
              // Extra Keys for severity
              ...(header.id.toLowerCase() === "severity" ? { 
                icon: filterIconMappings.severity[value.toLowerCase()],
                apiValue: value,
                name: getOCSFSeverityNameFromNumber(t, parseInt(value))
              } : {}),
            }));

            return {
              label: header.id,
              children: categoryChildren,
              isDate: header.isDate || false,
              isFilterable: header.isFilterable
            };
          } catch (error) {
            console.error(`Error creating children for ${header.id}:`, error);
            return {
              label: header.id,
              children: [],
              isDate: header.isDate || false,
              isFilterable: header.isFilterable
            };
          }
        }
        
        // If no match found at all
        return {
          label: header.id,
          children: [],
          isDate: header.isDate || false,
          isFilterable: header.isFilterable
        };
      }),
      count: 0
    };

    return(newFilter);
  };

  /**
   * Add the current filter build to the list of filters or update it's existing version in the list
   */
  const applyFilters = () => {    
    if (activeFilter) {
      // Aggregate list of children where isChecked is true
      const selectedFilters = getSelectedFilters(activeFilter)
 
      // Update the current filter list with the newly rebuilt filter: 'activeFilter'
      if (filters.some(filter => filter.id === activeFilter.id)) {
        setFilters(filters.map((filter) => {
          // Search for the old version of the filter
          if(filter.id === activeFilter.id) {
            // Swap the old version for the updated build
            return activeFilter
          } else {
            return filter
          }
        }));
      } else {
        // If there is no existing filter matching the current build, add it to the list
        setFilters((prevFilters) => [...prevFilters, activeFilter]);
      }

      setFilterData({filter: selectedFilters});
    }
  };


  /**
   * If the filter is open setup a base object for the filter to build upon
   */
  useEffect(() => {
    if (isFilterModalOpen) {
      setUnderConstructionFilter(getBaseFilter());
    }
  }, [isFilterModalOpen]);




  /**
   * React to the filter being saved in the SideModal
   */
  useEffect(() => {
    // setPage to 0, otherwise we may be lost on a page without data
    setPage(0);
    // Add the filter to the list or update it's version in said list
    applyFilters();
    // Close the modal
    setIsFilterModalOpen(false);
  }, [activeFilter]);


  return (
    <div style={{ width: '100%' }}>
      { underConstructionFilter && 
        <SideModal 
          isFilterModalOpen={isFilterModalOpen}
          setFilterModalOpen={setIsFilterModalOpen}
          filters={filters}
          setFilters={setFilters}
          activeFilter={activeFilter}
          setActiveFilter={setActiveFilter}
          underConstructionFilter={underConstructionFilter}
          setUnderConstructionFilter={setUnderConstructionFilter}
          getBaseFilter={getBaseFilter}
          framework={framework}
          descriptions={descriptions}
          selectedDescription={selectedDescription}
          selectedSectionTitle={selectedSectionTitle}
          onFilterCreated={onFilterCreated}
        />
      }
    </div>
  );
}
